import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import {
  Image,
  Plus,
  Trash2,
  Edit,
  Calendar,
  Tag,
  ExternalLink,
  Grid,
  List,
  Link as LinkIcon,
  Loader2,
  Sparkles,
  Upload
} from 'lucide-react';

const categories = [
  { value: 'events', label: 'Community Events' },
  { value: 'volunteers', label: 'Volunteer Activities' },
  { value: 'infrastructure', label: 'Infrastructure Progress' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'community', label: 'Community Life' },
  { value: 'general', label: 'General' }
];

const AdminGallery = () => {
  const { token } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [filterCategory, setFilterCategory] = useState('all');
  const [extracting, setExtracting] = useState(false);
  const [urlToExtract, setUrlToExtract] = useState('');
  const [addMode, setAddMode] = useState('upload'); // 'upload' or 'url'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    category: 'general',
    event_name: '',
    date_taken: ''
  });

  useEffect(() => {
    fetchPhotos();
  }, [token]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/gallery?limit=100`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtractUrl = async () => {
    if (!urlToExtract.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setExtracting(true);
    try {
      const response = await axios.post(`${API}/admin/gallery/extract-url`, 
        { url: urlToExtract },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const data = response.data;
      setFormData(prev => ({
        ...prev,
        title: data.title || prev.title,
        description: data.description || prev.description,
        image_url: data.image_url || prev.image_url
      }));
      
      toast.success('URL metadata extracted with AI description!');
    } catch (error) {
      console.error('Failed to extract URL:', error);
      toast.error('Failed to extract URL metadata. Try entering details manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.image_url.trim()) {
      toast.error('Please provide title and image');
      return;
    }

    try {
      if (editingPhoto) {
        await axios.patch(`${API}/admin/gallery/${editingPhoto.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo updated');
      } else {
        await axios.post(`${API}/admin/gallery/upload`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo added to gallery');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to save photo');
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      category: 'general',
      event_name: '',
      date_taken: ''
    });
    setEditingPhoto(null);
    setUrlToExtract('');
    setAddMode('upload');
  };

  const openEditDialog = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      title: photo.title || '',
      description: photo.description || '',
      image_url: photo.image_url || '',
      category: photo.category || 'general',
      event_name: photo.event_name || '',
      date_taken: photo.date_taken || ''
    });
    setAddMode('upload');
    setDialogOpen(true);
  };

  const filteredPhotos = filterCategory === 'all' 
    ? photos 
    : photos.filter(p => p.category === filterCategory);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Image className="w-8 h-8 text-plp-gold" />
            Photo Gallery
          </h1>
          <p className="text-slate-600 mt-1">Manage community photos and event galleries</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-plp-blue' : ''}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-plp-blue' : ''}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-plp-gold hover:bg-plp-gold/90 text-plp-blue" data-testid="add-photo-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Photo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPhoto ? 'Edit Photo' : 'Add Photo to Gallery'}</DialogTitle>
              </DialogHeader>
              
              {/* Mode Selector */}
              {!editingPhoto && (
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg mb-4">
                  <button
                    type="button"
                    onClick={() => setAddMode('upload')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      addMode === 'upload' 
                        ? 'bg-white text-plp-blue shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode('url')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      addMode === 'url' 
                        ? 'bg-white text-plp-blue shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    From URL (AI)
                  </button>
                </div>
              )}
              
              {/* URL Extraction Mode */}
              {addMode === 'url' && !editingPhoto && (
                <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                  <Label className="flex items-center gap-2 text-purple-700 mb-2">
                    <Sparkles className="w-4 h-4" />
                    Auto-Extract from Article URL
                  </Label>
                  <p className="text-xs text-slate-500 mb-3">
                    Paste a link to an article or webpage - we'll extract the image, title, and generate an AI description
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={urlToExtract}
                      onChange={(e) => setUrlToExtract(e.target.value)}
                      placeholder="https://example.com/article..."
                      className="flex-1"
                    />
                    <Button 
                      type="button"
                      onClick={handleExtractUrl}
                      disabled={extracting}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {extracting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Extract
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Image Upload/URL */}
                <ImageUploader
                  label="Photo *"
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  placeholder="https://example.com/photo.jpg"
                />
                
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Photo title"
                    data-testid="photo-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Photo description..."
                    rows={3}
                  />
                  {formData.description && addMode === 'url' && (
                    <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      AI-generated description
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Date Taken</Label>
                    <Input
                      type="date"
                      value={formData.date_taken}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_taken: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Event Name (optional)</Label>
                  <Input
                    value={formData.event_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                    placeholder="Community Cleanup Day"
                  />
                </div>
                
                <Button type="submit" className="w-full bg-plp-blue hover:bg-plp-blue/90" data-testid="save-photo-btn">
                  {editingPhoto ? 'Update Photo' : 'Add to Gallery'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filteredPhotos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Image className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No photos yet</h3>
            <p className="text-slate-500 mt-1">Add photos to showcase community events and activities</p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map((photo) => (
            <Card key={photo.id} className="overflow-hidden group">
              <div className="relative aspect-square">
                <img 
                  src={photo.image_url.startsWith('/api') ? `${API.replace('/api', '')}${photo.image_url}` : photo.image_url}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => openEditDialog(photo)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => handleDelete(photo.id)}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={() => window.open(photo.image_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-1 bg-plp-blue/90 text-white text-xs rounded-full">
                    {categories.find(c => c.value === photo.category)?.label || photo.category}
                  </span>
                </div>
              </div>
              <CardContent className="p-3">
                <h3 className="font-medium text-slate-800 truncate">{photo.title}</h3>
                {photo.event_name && (
                  <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-1">
                    <Tag className="w-3 h-3" /> {photo.event_name}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPhotos.map((photo) => (
            <Card key={photo.id}>
              <CardContent className="p-4 flex gap-4">
                <img 
                  src={photo.image_url.startsWith('/api') ? `${API.replace('/api', '')}${photo.image_url}` : photo.image_url}
                  alt={photo.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-slate-800">{photo.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{photo.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(photo)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(photo.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-full">
                      {categories.find(c => c.value === photo.category)?.label || photo.category}
                    </span>
                    {photo.event_name && (
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" /> {photo.event_name}
                      </span>
                    )}
                    {photo.date_taken && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {photo.date_taken}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
